import { FunctionComponent } from 'react'
import { Stack, Chip, Tooltip } from '@mui/material'
import {
  CloudSync as CloudSyncIcon,
  Error as ErrorIcon,
  Edit as EditIcon
} from '@mui/icons-material'
import { ProtocolOfflineStatus } from '../hooks/useProtocolOfflineStatus'

interface ProtocolOfflineIndicatorProps {
  status: ProtocolOfflineStatus
  compact?: boolean
}

const ProtocolOfflineIndicator: FunctionComponent<ProtocolOfflineIndicatorProps> = ({
  status,
  compact = false
}) => {
  // Safety check - if status is null or undefined, return null
  if (!status) {
    return null
  }

  const hasAnyOfflineData =
    status.hasPendingMutations || status.hasFailedMutations || status.hasDrafts

  if (!hasAnyOfflineData) {
    return null
  }

  const indicators = []

  // Failed mutations (highest priority)
  if (status.hasFailedMutations) {
    indicators.push(
      <Tooltip
        key='failed'
        title={`${status.failedCount} nieudanych operacji - wymagana interwencja`}
        arrow
      >
        <Chip
          icon={<ErrorIcon />}
          label={compact ? '' : status.failedCount}
          color='error'
          size='small'
          sx={{
            fontSize: '10px',
            height: compact ? '20px' : '22px',
            minWidth: compact ? '20px' : 'auto',
            '& .MuiChip-label': compact ? { px: 0 } : {},
            '& .MuiChip-icon': {
              margin: '0',
              fontSize: '14px'
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        />
      </Tooltip>
    )
  }

  // Pending mutations
  if (status.hasPendingMutations) {
    indicators.push(
      <Tooltip
        key='pending'
        title={`${status.pendingCount} operacji oczekuje na synchronizację`}
        arrow
      >
        <Chip
          icon={<CloudSyncIcon className='rotating' />}
          label={compact ? '' : status.pendingCount}
          color='warning'
          size='small'
          sx={{
            fontSize: '10px',
            height: compact ? '20px' : '22px',
            minWidth: compact ? '20px' : 'auto',
            '& .MuiChip-label': compact ? { px: 0 } : {},
            '& .MuiChip-icon': {
              margin: '0',
              fontSize: '14px'
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        />
      </Tooltip>
    )
  }

  // Form drafts
  if (status.hasDrafts) {
    indicators.push(
      <Tooltip
        key='drafts'
        title={`${status.draftsCount} niezapisanych roboczych wersji formularzy`}
        arrow
      >
        <Chip
          icon={<EditIcon />}
          label={compact ? '' : status.draftsCount}
          color='info'
          size='small'
          sx={{
            fontSize: '10px',
            height: compact ? '20px' : '22px',
            minWidth: compact ? '20px' : 'auto',
            '& .MuiChip-label': compact ? { px: 0 } : {},
            '& .MuiChip-icon': {
              margin: '0',
              fontSize: '14px'
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        />
      </Tooltip>
    )
  }

  return (
    <Stack direction='row' spacing={0.5} alignItems='center'>
      {indicators}

      {/* CSS for rotating animation */}
      <style>
        {`
          .rotating {
            animation: rotate 2s linear infinite;
          }
          
          @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Stack>
  )
}

export default ProtocolOfflineIndicator
